import { useMutation } from "@apollo/client";
import Upload from "@mui/icons-material/Upload";
import { Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import { gql } from "generated-graphql";
import { useCallback, useRef, useState } from "react";
import { uploadFileDirectly } from "util/uploadFile";

const DISPATCH_WASTE_PIPELINE_RUN_MUTATION = gql(`
  mutation DispatchWastePipelineRun($input: DispatchWastePipelineRunInput!) {
    dispatchWastePipelineRun(input: $input) {
      id
      status
      task
    }
  }
`);

const SEARCH_TENANTS_QUERY = gql(`
  query DispatchPipelineSearchTenants($search: String, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

type TenantOption = { id: string; name: string };

// Only pipelines that have been vetted for self-service use are listed here.
// Currently limited to waste-enabled pipelines with a V2 implementation.
const PIPELINE_OPTIONS = [{ label: "Clean Harbors", value: "clean-harbors" }] as const;

interface DispatchWastePipelineRunDialogProps {
  onClose: () => void;
}

export function DispatchWastePipelineRunDialog({ onClose }: DispatchWastePipelineRunDialogProps) {
  const alerts = useAlerts();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pipelineName, setPipelineName] = useState("clean-harbors");
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [dispatchMutation] = useMutation(DISPATCH_WASTE_PIPELINE_RUN_MUTATION);

  const canSubmit = pipelineName && selectedTenant && file && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!selectedTenant || !file) return;

    setSubmitting(true);
    try {
      const sourceS3Key = await uploadFileDirectly(file, {
        tenantId: selectedTenant.id,
      });

      const { data } = await dispatchMutation({
        variables: {
          input: {
            pipelineName,
            tenantId: selectedTenant.id,
            sourceS3Key,
          },
        },
      });

      const jobId = data?.dispatchWastePipelineRun.id;
      alerts.success(`Pipeline dispatched successfully. Job ID: ${jobId}`);
      onClose();
    } catch (err) {
      alerts.error(
        `Failed to dispatch pipeline: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [selectedTenant, file, pipelineName, dispatchMutation, alerts, onClose]);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const selected = event.target.files?.[0];
    if (selected) {
      setFile(selected);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="New Pipeline Run"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: "Dispatch",
        loading: submitting,
        disabled: !canSubmit,
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <TextField
          select
          label="Pipeline Type"
          value={pipelineName}
          onChange={(e) => setPipelineName(e.target.value)}
          fullWidth
        >
          {PIPELINE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <EncampAutocomplete
          query={SEARCH_TENANTS_QUERY}
          getItems={(data) => data?.tenants.items ?? []}
          getOptionLabel={(option) => option.name}
          variables={{
            search: tenantSearch,
            pageSize: 25,
            sort: [{ field: "name", sort: "asc" as const }],
          }}
          value={selectedTenant}
          onChange={(value) => setSelectedTenant(value)}
          onInputChange={(value) => setTenantSearch(value)}
          label="Organization"
        />

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button variant="outlined" component="label" startIcon={<Upload />}>
            Choose File
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              hidden
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary">
              {file.name}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
